/**
 * Extra per-department data — courses, sections — used on the
 * Department detail page. Generated deterministically from the dept
 * code + intake so every page resolves the same content.
 */

import { AREAS } from "./faculty";

export interface DeptCourse {
  code: string;
  name: string;
  credits: number;
  semester: number;
  type: "core" | "elective" | "lab" | "humanities" | "open-elective";
}

const SHARED_FIRST_YEAR = [
  { code: "MA101", name: "Engineering Mathematics I", credits: 4, type: "core" as const },
  { code: "PH101", name: "Engineering Physics", credits: 4, type: "core" as const },
  { code: "CH101", name: "Engineering Chemistry", credits: 4, type: "core" as const },
  { code: "ME101", name: "Engineering Drawing", credits: 3, type: "core" as const },
  { code: "CS101", name: "Programming for Problem Solving", credits: 4, type: "core" as const },
  { code: "HU101", name: "Communicative English", credits: 3, type: "humanities" as const },
  { code: "MA102", name: "Engineering Mathematics II", credits: 4, type: "core" as const },
  { code: "WS101", name: "Workshop Practice", credits: 3, type: "lab" as const },
  { code: "ENV101", name: "Environmental Studies", credits: 3, type: "humanities" as const },
];

/** Standard course-name templates we mix with the dept's AREAS list. */
const TEMPLATES = [
  "Foundations of",
  "Applied",
  "Advanced",
  "Computational",
  "Topics in",
  "Principles of",
];

/**
 * Generate semester 3-8 courses for a department from its AREAS list.
 * Deterministic — same dept code always produces the same course list.
 */
function generateCourses(code: string): DeptCourse[] {
  const out: DeptCourse[] = [];
  // First year — shared with all branches but tagged with the dept code.
  SHARED_FIRST_YEAR.slice(0, 6).forEach((c, i) => {
    out.push({ ...c, semester: 1 } as DeptCourse);
  });
  SHARED_FIRST_YEAR.slice(6).forEach((c) => {
    out.push({ ...c, semester: 2 } as DeptCourse);
  });

  const ar = AREAS[code] || ["Foundations"];
  // Branch courses: 5 per semester for 3-8.
  for (let sem = 3; sem <= 8; sem++) {
    for (let i = 0; i < 5; i++) {
      const areaIdx = (sem * 5 + i) % ar.length;
      const tplIdx = (sem + i) % TEMPLATES.length;
      const courseNum = sem * 100 + (i + 1);
      const codeStr = `${code}${courseNum}`;
      const isElective = i === 4 && sem >= 5;
      const isLab = i === 3 && sem <= 6;
      const isOpen = i === 4 && sem >= 6;
      out.push({
        code: codeStr,
        name: isLab
          ? `${ar[areaIdx]} Laboratory`
          : `${TEMPLATES[tplIdx]} ${ar[areaIdx]}`,
        credits: isLab ? 2 : isElective ? 3 : 4,
        semester: sem,
        type: isOpen ? "open-elective" : isElective ? "elective" : isLab ? "lab" : "core",
      });
    }
  }
  return out;
}

const COURSE_CACHE = new Map<string, DeptCourse[]>();

export function getDeptCourses(code: string): DeptCourse[] {
  if (!COURSE_CACHE.has(code)) {
    COURSE_CACHE.set(code, generateCourses(code));
  }
  return COURSE_CACHE.get(code)!;
}

export const SEMESTER_LABEL = (s: number): string =>
  ["", "I", "II", "III", "IV", "V", "VI", "VII", "VIII"][s] ?? String(s);

export const COURSE_TYPE_LABEL: Record<DeptCourse["type"], string> = {
  core: "Core",
  elective: "Elective",
  lab: "Lab",
  humanities: "HU",
  "open-elective": "Open elective",
};
